// src/pages/appointments/summary/AppointmentSummary.jsx
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import TopBar from "@/sot/primitives/TopBar.jsx";
import AvatarThumb68 from "@/sot/primitives/AvatarThumb68.jsx";
import Checkbox from "@/components/ui/Checkbox.jsx";
import Card from "@/components/ui/Card.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import supabase from "@/services/supabaseClient";
import { loadDoctorAppointmentSnapshot } from "@/pages/doctors/appointments/lib/doctorAppointmentsData.js";
import { loadPatientAppointmentSnapshot } from "@/services/patientAppointmentSnapshots.js";
import { loadVitalRepNoteByAppointment } from "@/pages/doctors/appointments/lib/vitalRepNote.js";
import { resolvePatientFacingDoctorName } from "@/utils/doctorPatientFacingName.js";

const ICONS = {
  doneAll: "/icons/appointments/video-call/done_all.svg",
};

function safeDate(value) {
  try {
    const d = value ? new Date(value) : null;
    return d && !Number.isNaN(d.getTime()) ? d : null;
  } catch {
    return null;
  }
}

function pickFirstText(...values) {
  for (const value of values) {
    const text = String(value || "").trim();
    if (text) return text;
  }
  return "";
}

function isAvatarPlaceholder(value) {
  const src = String(value || "").trim();
  return (
    !src ||
    src.includes("avatar_placeholder") ||
    src.includes("circular-avatar-placeholder") ||
    src.includes("Profile_Avatar")
  );
}

async function resolveDoctorAvatarUrl(doctor = {}) {
  const direct = pickFirstText(
    doctor?.photo_url,
    doctor?.avatar_url,
    doctor?.profile_photo_url,
    doctor?.profile_image_url,
    doctor?.avatar
  );
  if (
    direct &&
    !isAvatarPlaceholder(direct) &&
    !direct.includes("/storage/v1/object/public/doctor-profile-images/private/")
  ) {
    return direct;
  }

  const rawPath = pickFirstText(
    doctor?.profile_photo_path,
    doctor?.photo_path,
    doctor?.avatar_path
  );
  if (!rawPath) return direct;
  if (/^https?:\/\//i.test(rawPath) && !rawPath.includes("/storage/v1/object/public/doctor-profile-images/private/")) {
    return rawPath;
  }

  const cleanPath = rawPath
    .replace(/^\/+/, "")
    .replace(/^public\//, "")
    .replace(/^doctor-profile-images\//, "");
  if (!cleanPath) return direct;

  try {
    const { data, error } = await supabase.storage
      .from("doctor-profile-images")
      .createSignedUrl(cleanPath, 60 * 60 * 24);
    if (!error && data?.signedUrl) return data.signedUrl;
  } catch {
    // keep fallback path below
  }

  return direct;
}

function looksLikeUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value || "").trim()
  );
}

function isPrescriptionPermissionError(error) {
  const message = String(error?.message || error || "").toLowerCase();
  return message.includes("permission denied") && message.includes("prescriptions");
}

function meaningfulCounterpartName(...values) {
  for (const value of values) {
    const text = String(value || "").trim();
    const normalized = text.toLowerCase();
    if (!text) continue;
    if (
      normalized === "patient" ||
      normalized === "doctor" ||
      normalized === "withpatient" ||
      normalized === "withdoctor"
    ) {
      continue;
    }
    return text;
  }
  return "";
}

export default function AppointmentSummary() {
  const nav = useNavigate();
  const { t, formatDate } = useLanguage();
  const auth = useAuth() || {};
  const { id } = useParams();
  const location = useLocation();
  const state = location.state || null;
  const [rxBusy, setRxBusy] = React.useState(false);
  const [rxErr, setRxErr] = React.useState("");
  const [latestPrescriptionId, setLatestPrescriptionId] = React.useState(() =>
    String(state?.prescriptionId || state?.rxId || "").trim()
  );
  const inferredActorRole = React.useMemo(() => {
    const explicitActorRole = String(state?.actorRole || "").trim().toLowerCase();
    if (explicitActorRole === "doctor" || explicitActorRole === "patient") {
      return explicitActorRole;
    }

    const profile = auth?.profile || {};
    const profileRole = String(profile?.role || "").trim().toLowerCase();
    if (profileRole === "doctor" || (profile?.hasDoctor && !profile?.hasPatient)) {
      return "doctor";
    }
    if (profileRole === "patient" || (!profile?.hasDoctor && profile?.hasPatient)) {
      return "patient";
    }

    return "patient";
  }, [auth?.profile, state?.actorRole]);
  const actorRole = inferredActorRole === "doctor" ? "doctor" : "patient";
  const [hydratedCounterpart, setHydratedCounterpart] = React.useState({
    name: "",
    avatar: "",
  });
  const [vitalRepNote, setVitalRepNote] = React.useState(null);
  const patientId =
    state?.patientId ||
    state?.patient?.user_id ||
    state?.patient?.id ||
    state?.restored?.patientId ||
    state?.restored?.patient_id ||
    null;

  const counterpartName =
    actorRole === "doctor"
      ? meaningfulCounterpartName(
          state?.counterpartName,
          state?.patient?.full_name,
          state?.patient?.name,
          hydratedCounterpart.name
        ) ||
        state?.patient?.full_name ||
        state?.patient?.name ||
        hydratedCounterpart.name ||
        t("appointments.summary.patientFallback", "Patient")
      : resolvePatientFacingDoctorName(
          state?.counterpartName,
          hydratedCounterpart.name,
          hydratedCounterpart,
          state?.doctor,
          state?.doctorName,
          t("appointments.summary.doctorFallback", "Doctor")
        );

  const counterpartAvatar =
    actorRole === "doctor"
      ? state?.counterpartAvatar ||
        state?.patient?.avatar_url ||
        state?.patient?.profile_image_url ||
        hydratedCounterpart.avatar ||
        state?.otherThumb ||
        "/icons/circular-avatar-placeholder.svg"
      : state?.counterpartAvatar ||
        hydratedCounterpart.avatar ||
        state?.doctor?.photo_url ||
        state?.doctor?.avatar_url ||
        state?.doctor?.profile_photo_url ||
        state?.otherThumb ||
        "/icons/circular-avatar-placeholder.svg";

  const hasValidAppointmentId = looksLikeUuid(id);
  const careSubjectType = String(
    state?.careSubjectType ||
      state?.care_subject_type ||
      state?.patient?.care_subject_type ||
      "self"
  )
    .trim()
    .toLowerCase();
  const careSubjectName = String(
    state?.careSubjectName ||
      state?.care_subject_name ||
      state?.patient?.name ||
      state?.patientName ||
      ""
  ).trim();
  const careSubjectConfirmationRequired =
    careSubjectType === "dependant" && Boolean(careSubjectName);
  const [careSubjectConfirmed, setCareSubjectConfirmed] = React.useState(
    !careSubjectConfirmationRequired
  );
  const [appointmentState, setAppointmentState] = React.useState(() => ({
    status: String(state?.restored?.status || "").trim().toLowerCase(),
    notes: String(
      state?.restored?.diagnosisNotes ||
        state?.restored?.notes ||
        state?.notes ||
        ""
    ).trim(),
    diagnosisTitle: String(
      state?.restored?.diagnosisTitle ||
        state?.restored?.diagnosis ||
        state?.diagnosisTitle ||
        ""
    ).trim(),
    videoStatus: String(state?.restored?.video_status || "").trim().toLowerCase(),
    videoEndedAt: String(state?.restored?.video_ended_at || state?.endedAt || "").trim(),
    appointmentDate: String(
      state?.restored?.appointment_date ||
        state?.appointmentDate ||
        state?.appointment_date ||
        ""
    ).trim(),
    durationMinutes: Number(
      state?.restored?.duration_minutes ||
        state?.durationMinutes ||
        state?.duration_minutes ||
        0
    ) || 0,
  }));

  React.useEffect(() => {
    setCareSubjectConfirmed(!careSubjectConfirmationRequired);
  }, [careSubjectConfirmationRequired, careSubjectName]);

  React.useEffect(() => {
    let alive = true;

    async function hydrateAppointmentState() {
      if (!id || !hasValidAppointmentId) return;
      try {
        const { data, error } = await supabase
          .from("appointments")
          .select(
            "status, notes, diagnosis_title, diagnosis_notes, video_status, video_ended_at, appointment_date, duration_minutes"
          )
          .eq("id", id)
          .maybeSingle();

        if (error || !alive || !data) return;

        setAppointmentState((current) => ({
          status: String(data.status || current.status || "").trim().toLowerCase(),
          notes: String(
            data.diagnosis_notes || data.notes || current.notes || ""
          ).trim(),
          diagnosisTitle: String(
            data.diagnosis_title || current.diagnosisTitle || ""
          ).trim(),
          videoStatus: String(
            data.video_status || current.videoStatus || ""
          ).trim().toLowerCase(),
          videoEndedAt: String(
            data.video_ended_at || current.videoEndedAt || ""
          ).trim(),
          appointmentDate: String(
            data.appointment_date || current.appointmentDate || ""
          ).trim(),
          durationMinutes: Number(data.duration_minutes || current.durationMinutes || 0) || 0,
        }));
      } catch {
        // keep summary resilient if hydration fails
      }
    }

    hydrateAppointmentState();
    return () => {
      alive = false;
    };
  }, [hasValidAppointmentId, id]);

  React.useEffect(() => {
    const explicitRxId = String(state?.prescriptionId || state?.rxId || "").trim();
    if (explicitRxId) {
      setLatestPrescriptionId(explicitRxId);
      return;
    }

    let alive = true;

    async function hydrateLatestPrescription() {
      if (!id || !hasValidAppointmentId) {
        if (alive) setLatestPrescriptionId("");
        return;
      }
      try {
        const { data: latestPrescription, error } = await supabase
          .from("prescriptions")
          .select("id, issued_at, created_at")
          .eq("appointment_id", id)
          .order("issued_at", { ascending: false, nullsFirst: false })
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) throw error;
        if (!alive) return;
        setLatestPrescriptionId(String(latestPrescription?.id || "").trim());
      } catch {
        if (!alive) return;
        setLatestPrescriptionId("");
      }
    }

    hydrateLatestPrescription();
    return () => {
      alive = false;
    };
  }, [hasValidAppointmentId, id, state?.prescriptionId, state?.rxId]);

  React.useEffect(() => {
    let alive = true;

    async function hydrateSummaryCounterpart() {
      if (!hasValidAppointmentId) return;
      if (state?.counterpartName && state?.counterpartAvatar) return;

      try {
        if (actorRole === "doctor") {
          const snapshot = await loadDoctorAppointmentSnapshot({
            appointmentId: id,
            doctorId: auth?.user?.id || null,
            supabase,
          });

          if (!alive || !snapshot) return;

          setHydratedCounterpart({
            name: String(
              snapshot?.patientName ||
                snapshot?.patient?.full_name ||
                snapshot?.patient?.name ||
                ""
            ).trim(),
            avatar: String(
              snapshot?.patient?.avatar_url ||
                snapshot?.patient?.profile_image_url ||
                ""
            ).trim(),
          });
          return;
        }

        const snapshot = await loadPatientAppointmentSnapshot(id, {
          fallback: state?.restored || state || null,
        });
        if (!alive || !snapshot) return;

        setHydratedCounterpart({
          name: resolvePatientFacingDoctorName(
            snapshot?.doctor,
            snapshot?.doctorName,
            state?.doctor,
            state?.doctorName,
          ),
          avatar: await resolveDoctorAvatarUrl(snapshot?.doctor || state?.doctor || {}),
        });
      } catch {
        if (!alive) return;
        setHydratedCounterpart({ name: "", avatar: "" });
      }
    }

    hydrateSummaryCounterpart();
    return () => {
      alive = false;
    };
  }, [
    actorRole,
    auth?.user?.id,
    hasValidAppointmentId,
    id,
    state?.counterpartAvatar,
    state?.counterpartName,
  ]);

  React.useEffect(() => {
    let alive = true;

    async function hydrateVitalRep() {
      if (!id || !hasValidAppointmentId) {
        if (alive) setVitalRepNote(null);
        return;
      }

      try {
        const nextNote = await loadVitalRepNoteByAppointment(id);
        if (!alive) return;
        setVitalRepNote(nextNote);
      } catch {
        if (!alive) return;
        setVitalRepNote(null);
      }
    }

    hydrateVitalRep();
    const handleVitalRepChanged = () => {
      hydrateVitalRep();
    };
    window.addEventListener("afrimed:vitalrep:changed", handleVitalRepChanged);
    return () => {
      alive = false;
      window.removeEventListener("afrimed:vitalrep:changed", handleVitalRepChanged);
    };
  }, [hasValidAppointmentId, id]);

  const callEnded = Boolean(
    state?.endedAt ||
      appointmentState.videoEndedAt ||
      appointmentState.status === "completed" ||
      appointmentState.videoStatus === "completed"
  );
  const consultationNotesReady = Boolean(
    appointmentState.diagnosisTitle || appointmentState.notes
  );
  const vitalRepDraftReady = Boolean(vitalRepNote?.generatedDraftText);
  const vitalRepFinalized = vitalRepNote?.status === "finalized" && Boolean(vitalRepNote?.finalNoteText);
  const ehrUpdated = Boolean(
    state?.ehrUpdatedAt ||
      state?.restored?.ehrUpdatedAt ||
      state?.ehrUpdated === true ||
      state?.restored?.ehrUpdated === true
  );
  const prescriptionReady = Boolean(latestPrescriptionId);
  const canOpenPrescription =
    actorRole === "doctor"
      ? consultationNotesReady || prescriptionReady
      : consultationNotesReady || prescriptionReady;
  const actualEndedAt = pickFirstText(
    state?.endedAt,
    state?.restored?.video_ended_at,
    appointmentState.videoEndedAt
  );
  const scheduledAt = pickFirstText(
    appointmentState.appointmentDate,
    state?.restored?.appointment_date,
    state?.appointmentDate,
    state?.appointment_date
  );
  const displayTimestamp = actualEndedAt || scheduledAt;
  const displayDate = safeDate(displayTimestamp);
  const endedAtLabel = displayDate
    ? formatDate(displayDate, {
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";
  const timeLabel = actualEndedAt
    ? t("appointments.summary.endedAt", "Ended: {datetime}", {
        datetime: endedAtLabel,
      })
    : scheduledAt
      ? t("appointments.summary.scheduledAt", "Scheduled for: {datetime}", {
          datetime: endedAtLabel,
        })
      : t("appointments.summary.timeUnavailable", "Appointment time unavailable");

  const headerTitle = consultationNotesReady
    ? actorRole === "doctor"
      ? vitalRepFinalized
        ? "Consultation note saved"
        : vitalRepDraftReady
          ? "VitalRep draft ready"
          : "Consultation notes saved"
      : vitalRepFinalized
        ? "Clinician notes saved"
        : "Consultation notes saved"
    : t("appointments.summary.status.completeTitle", "Consultation complete");

  const headerBody = actorRole === "doctor"
    ? consultationNotesReady
      ? vitalRepFinalized
        ? "Consultation note saved. Continue with prescription, tests, and follow-up actions."
        : vitalRepDraftReady
          ? "VitalRep draft is ready. Review and edit it from appointment details before finalizing."
          : "Diagnosis and notes are saved. Continue with prescription, tests, and follow-up actions."
      : t(
          "appointments.summary.status.completeBodyDoctor",
          "The consultation has ended. Continue the post-consultation workflow from the appointment details page."
        )
    : consultationNotesReady
      ? vitalRepFinalized
        ? "The consultation has ended. Your clinician finalized the consultation note. Prescription and follow-up updates remain available in AfriMed™."
        : "The consultation has ended. Prescription and visit updates remain available in AfriMed™."
      : t(
          "appointments.summary.status.completeBodyPatient",
          "The consultation has ended."
        );

  const steps = [
    {
      key: "ended",
      label: t("appointments.summary.steps.ended", "Call ended"),
      done: callEnded,
    },
    {
      key: "notes",
      label: actorRole === "doctor"
        ? vitalRepFinalized
          ? "Consultation note saved"
          : vitalRepDraftReady
            ? "VitalRep draft ready"
            : "Consultation notes saved"
        : vitalRepFinalized
          ? "Clinician notes saved"
          : "Consultation notes saved",
      done: consultationNotesReady,
    },
    ...(ehrUpdated
      ? [
          {
            key: "ehr",
            label: t("appointments.summary.steps.ehr", "EHR updated"),
            done: true,
          },
        ]
      : []),
    ...(actorRole === "doctor" || prescriptionReady
      ? [
          {
            key: "rx",
            label: t("appointments.summary.steps.prescription", "Prescription ready"),
            done: prescriptionReady,
          },
        ]
      : []),
  ];

  const doctorAppointmentDetailsHref = `/doctors/appointments/${String(id)}`;
  const doctorReturnState = {
    ...(state || {}),
    returnTo: doctorAppointmentDetailsHref,
    returnState: state || null,
  };
  const goBackToPrimaryDestination = React.useCallback(() => {
    if (actorRole === "doctor") {
      nav(doctorAppointmentDetailsHref, {
        replace: true,
        state: doctorReturnState,
      });
      return;
    }

    nav("/appointments", { replace: true });
  }, [actorRole, doctorAppointmentDetailsHref, doctorReturnState, nav]);

  async function openPrescription() {
    if (rxBusy) return;

    try {
      setRxBusy(true);
      setRxErr("");

      const explicitRxId = latestPrescriptionId || state?.prescriptionId || state?.rxId || null;
      if (explicitRxId) {
        nav(`/appointments/prescriptions/${String(explicitRxId)}`, {
          state: {
            from: "appointment-summary",
            actorRole,
            appointmentId: String(id),
            patientId,
            patient: state?.patient || null,
            restored: state?.restored || null,
            prescriptionId: String(explicitRxId),
            returnTo:
              actorRole === "doctor"
                ? doctorAppointmentDetailsHref
                : location.pathname,
            returnState:
              actorRole === "doctor" ? doctorReturnState : state,
          },
        });
        return;
      }

      if (!hasValidAppointmentId) {
        throw new Error(
          t(
            "appointments.summary.errors.prescriptionUnavailable",
            "Prescription not yet available for this appointment."
          )
        );
      }

      const { data: latestPrescription, error } = await supabase
        .from("prescriptions")
        .select("id, issued_at, created_at")
        .eq("appointment_id", id)
        .order("issued_at", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        if (actorRole === "doctor" && isPrescriptionPermissionError(error)) {
          nav(`/appointments/prescriptions/new/${String(id)}`, {
            state: {
              from: "appointment-summary",
              actorRole,
              appointmentId: String(id),
              patientId,
              patient: state?.patient || null,
              restored: state?.restored || null,
              returnTo:
                actorRole === "doctor"
                  ? doctorAppointmentDetailsHref
                  : location.pathname,
              returnState:
                actorRole === "doctor" ? doctorReturnState : state,
            },
          });
          return;
        }
        throw error;
      }
      if (!latestPrescription?.id) {
        if (actorRole === "doctor") {
          nav(`/appointments/prescriptions/new/${String(id)}`, {
            state: {
              from: "appointment-summary",
              actorRole,
              appointmentId: String(id),
              patientId,
              patient: state?.patient || null,
              restored: state?.restored || null,
              returnTo:
                actorRole === "doctor"
                  ? doctorAppointmentDetailsHref
                  : location.pathname,
              returnState:
                actorRole === "doctor" ? doctorReturnState : state,
            },
          });
          return;
        }
        throw new Error(
          t(
            "appointments.summary.errors.prescriptionUnavailable",
            "Prescription not yet available for this appointment."
          )
        );
      }

      nav(`/appointments/prescriptions/${String(latestPrescription.id)}`, {
        state: {
          from: "appointment-summary",
          actorRole,
          appointmentId: String(id),
          patientId,
          patient: state?.patient || null,
          restored: state?.restored || null,
          prescriptionId: String(latestPrescription.id),
          returnTo:
            actorRole === "doctor"
              ? doctorAppointmentDetailsHref
              : location.pathname,
          returnState:
            actorRole === "doctor" ? doctorReturnState : state,
        },
      });
    } catch (e) {
      setRxErr(
        e?.message ||
          t(
            "appointments.summary.errors.openPrescription",
            "Unable to open prescription right now."
          )
      );
    } finally {
      setRxBusy(false);
    }
  }

  const primaryCtaLabel =
    actorRole === "doctor"
      ? t("appointments.summary.actions.backToAppointmentDetails", "Back to appointment details")
      : ehrUpdated
        ? t("appointments.summary.actions.viewEhrUpdate", "View EHR update")
        : t("appointments.summary.actions.backToAppointments", "Back to Appointments");
  const homeHref = actorRole === "doctor" ? "/home/doctor" : "/home/patient";
  const primaryCtaAction = () => {
    if (actorRole === "doctor") {
      goBackToPrimaryDestination();
      return;
    }

    if (!ehrUpdated) {
      nav("/appointments");
      return;
    }

    nav("/settings/patient/p/ehr", {
      state: {
        appointmentId: String(id),
        prescriptionId: latestPrescriptionId || null,
        patientId,
        patient: state?.patient || null,
        from: "appointment-summary",
        returnTo: location.pathname,
        returnState: state,
      },
    });
  };

  const openDoctorReferral = () => {
    nav(`/doctors/appointments/${String(id)}/refer`, {
      state: {
        appointmentId: String(id),
        patientUserId: patientId || null,
        doctorUserId: null,
        from: "appointment-summary",
        returnTo: doctorAppointmentDetailsHref,
        returnState: doctorReturnState,
      },
    });
  };

  return (
    <div className="min-h-screen">
      <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
        <TopBar
          title={t("appointments.summary.title", "Consultation complete")}
          subtitle={t("appointments.summary.subtitle", "Appointment #{id}", { id })}
          onBack={goBackToPrimaryDestination}
        />

        {/* Card 1: Doctor MiniCard */}
        <Card className="px-4 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-label-medium text-[rgb(var(--clr-on-surface-variant))]">
                {t(
                  "appointments.summary.finishedWith",
                  "You just finished your appointment with"
                )}
              </div>

              <div className="mt-1 text-body-large text-[rgb(var(--clr-on-surface))] truncate">
                {counterpartName}
              </div>

              <div className="mt-2 text-body-medium text-[rgb(var(--clr-on-surface))] opacity-80">
                {timeLabel}
              </div>
            </div>

            {/* Use AfriMed primitive for correct border + feel */}
            <div className="shrink-0">
              <AvatarThumb68
                src={counterpartAvatar}
                alt={counterpartName}
                // If your primitive supports className, this will ensure the corners feel “8px-ish”
                className="rounded-[8px]"
              />
            </div>
          </div>
        </Card>

        {/* Card 2: consultation status */}
        <Card className="mt-4 px-4 py-4">
          <div className="text-title-small text-[rgb(var(--clr-on-surface))]">
            {t("appointments.summary.consultationStatus", "Consultation status")}
          </div>

          <div className="mt-3 flex items-start gap-3">
            {/* Status Dot (premium pulse) */}
            <div className="relative mt-1">
              <div className="h-10 w-10 rounded-full bg-[rgba(2,6,23,0.06)] flex items-center justify-center relative overflow-visible">
                <span
                    className={[
                        "absolute inline-flex h-6 w-6 rounded-full",
                        "animate-ping",
                        consultationNotesReady ? "opacity-20" : "opacity-100",
                    ].join(" ")}
                    style={{
                        background: "rgba(var(--afrimed-secondary), 0.35)",
                        animationDuration: consultationNotesReady ? "3s" : "1.2s",
                    }}
                    />    

                <span
                  className="h-3 w-3 rounded-full"
                  style={{ background: "rgb(var(--afrimed-secondary))" }}
                />
              </div>
            </div>

            <div className="flex-1">
              <div className="text-body-large text-[rgb(var(--clr-on-surface))]">
                {headerTitle}
              </div>

              <div className="mt-1 text-body-medium leading-[20px] text-[rgb(var(--clr-on-surface-variant))]">
                {headerBody}
              </div>

              {/* mini timeline */}
              <div className="mt-3 space-y-2">
                {steps.map((s) => (
                  <div key={s.key} className="flex items-center gap-2">
                    {s.done ? (
                      <img
                        src={ICONS.doneAll}
                        alt=""
                        className="h-4 w-4"
                        draggable="false"
                      />
                    ) : (
                      <span
                        className="inline-block h-2.5 w-2.5 rounded-full"
                        style={{ background: "rgba(2,6,23,0.18)" }}
                      />
                    )}

                    <div className="text-body-medium text-[rgb(var(--clr-on-surface))] opacity-80">
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

        {/* CTAs */}
        <div className="mt-5 flex flex-col gap-3">
        {careSubjectConfirmationRequired ? (
          <div className="rounded-[12px] border border-[rgba(var(--clr-outline),0.35)] bg-[rgba(var(--afrimed-primary),0.04)] px-3 py-3">
            <label className="flex items-start gap-2">
              <Checkbox
                checked={careSubjectConfirmed}
                onChange={() => setCareSubjectConfirmed((prev) => !prev)}
              />
              <span className="text-body-medium text-[rgb(var(--clr-on-surface))]">
                {t(
                  "appointments.summary.confirmCareSubject",
                  "I confirm this appointment is for {name}.",
                  { name: careSubjectName }
                )}
              </span>
            </label>
          </div>
        ) : null}

        <div className="flex items-stretch gap-3">
        <button
            type="button"
            className={[
            "w-full min-w-0 h-12 rounded-[12px] px-3",
            "text-label-large text-center text-white",
            "bg-[rgb(var(--afrimed-primary))]",
            "shadow-[0_1px_0_rgba(2,6,23,0.06)]",
            ].join(" ")}
            onClick={primaryCtaAction}
            disabled={careSubjectConfirmationRequired && !careSubjectConfirmed}
            aria-disabled={careSubjectConfirmationRequired && !careSubjectConfirmed}
            style={
              careSubjectConfirmationRequired && !careSubjectConfirmed
                ? { opacity: 0.55, cursor: "not-allowed" }
                : undefined
            }
        >
          {primaryCtaLabel}
        </button>
        </div>

        {/* Tertiary: two text CTAs */}
        <div className="mt-1 flex items-center justify-center gap-6">
            <button
            type="button"
            className="text-label-large text-[rgb(var(--afrimed-primary))]"
            onClick={openPrescription}
            disabled={!canOpenPrescription}
            aria-disabled={!canOpenPrescription}
            style={!canOpenPrescription ? { opacity: 0.4 } : undefined}
            >
            {rxBusy
              ? t("appointments.summary.actions.opening", "Opening...")
              : actorRole === "doctor"
                ? t("appointments.summary.actions.prescription", "Prescription")
                : t("appointments.summary.actions.viewPrescription", "View prescription")}
            </button>

            <button
            type="button"
            className="text-label-large text-[rgb(var(--afrimed-primary))]"
            onClick={() => nav(homeHref)}
            >
            {t("appointments.summary.actions.goHome", "Go home")}
            </button>

            {actorRole === "doctor" ? (
              <button
                type="button"
                className="text-label-large text-[rgb(var(--afrimed-primary))]"
                onClick={openDoctorReferral}
              >
                {t("referrals.doctor.title", "Refer patient")}
              </button>
            ) : null}
        </div>

        {rxErr ? (
          <div className="mt-3 text-body-small text-[rgb(var(--schemes-critical))] text-center">
            {rxErr}
          </div>
        ) : null}
        </div>
        </Card>
      </div>
    </div>
  );
}
